import { CTABand } from "../../components/site/CTABand";
import { TrustBand } from "../../components/site/TrustBand";
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Layers, Building2, Workflow, ShieldCheck, ChevronDown, Compass, FileSearch, PenTool, PackageCheck, Cable, Blocks, ClipboardCheck, BookOpen, Headset, Wrench, RefreshCcw, Target, Puzzle, Gauge, TrendingUp, Handshake, Network, Camera, Server, PhoneCall, Zap, Cpu, Newspaper, Cctv, KeyRound, MapPin, Fingerprint, FireExtinguisher, BatteryCharging, CalendarCheck, Code2 } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { Chapter } from "../../components/site/Chapter";
import { CompanyVideo } from "../../components/site/CompanyVideo";
import { HolidayBanner } from "../../components/site/HolidayBanner";
import { HeroGlobe } from "../../components/site/HeroGlobe";
import { Marquee } from "../../components/site/Marquee";
import { NetworkCanvas } from "../../components/NetworkCanvas";
import { KeralaMap } from "../../components/KeralaMap";
import { EnquiryForm, ContactAside } from "../../components/EnquiryForm";
import { Reveal, MaskedLine, FadeIn, Stagger, StaggerItem } from "../../components/motion/Reveal";
import { scrollToId } from "../../lib/lenis";
import { CAPABILITIES, DISTRICTS } from "../../data/content";
import { SOLUTION_DETAILS } from "../../data/solutions";
import { SERVICES } from "../../data/services";
import { INDUSTRY_DETAILS } from "../../data/industries";
import { PROJECT_CATEGORIES } from "../../data/projects";
import { CASE_STUDIES } from "../../data/caseStudies";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { ARTICLES } from "../../data/articles";
import { TECH_CATEGORIES } from "../../data/technology";

const CAP_ICONS = [Code2, Layers, Building2, Workflow, ShieldCheck];

const SERVICE_ICONS = [Code2, Compass, FileSearch, PenTool, PackageCheck, Cable, Blocks, ClipboardCheck, BookOpen, ShieldCheck, Headset, Wrench, RefreshCcw, RefreshCcw, Target, Workflow, Zap];

const WHY = [
  { icon: Target, title: "Solution-oriented", text: "Design from your requirement — never from stock to clear." },
  { icon: Puzzle, title: "Integrated", text: "Security, network, communication and IT engineered as one system." },
  { icon: Gauge, title: "Engineering-driven", text: "Surveys, documentation and testing behind every decision." },
  { icon: TrendingUp, title: "Scalable", text: "Infrastructure that grows without being rebuilt." },
  { icon: RefreshCcw, title: "Lifecycle-focused", text: "We don't disappear after installation — AMC and support built in." },
  { icon: Handshake, title: "One technology partner", text: "Consulting to long-term support under one accountable roof." },
];

const TECH_ICONS = [Network, Camera, Server, PhoneCall, Zap, Cpu, Blocks];

const PROCESS = [
  { no: "01", title: "Consult & Survey", text: "We listen first, then assess your site anywhere in Kerala — physical, wireless and infrastructure." },
  { no: "02", title: "Design & Engineer", text: "A documented design: architecture, bill of quantities and phasing, aligned to your budget." },
  { no: "03", title: "Deliver & Integrate", text: "Supply, installation, configuration and integration — tested and commissioned before handover." },
  { no: "04", title: "Support & Manage", text: "AMC, managed services and on-site support across all fourteen districts, for the long term." },
];

const HERO_SERVICES = [
  { icon: Code2, label: "Software" },
  { icon: KeyRound, label: "Access Control" },
  { icon: Fingerprint, label: "Biometric Systems" },
  { icon: Network, label: "Networking" },
  { icon: Cctv, label: "CCTV" },
  { icon: FireExtinguisher, label: "Fire Alarm" },
  { icon: BatteryCharging, label: "UPS" },
  { icon: CalendarCheck, label: "Annual Maintenance Contracts" },
];

const QuickQuote = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const inputCls = "w-full bg-white/[0.05] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    if (form.phone.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid 10-digit phone number.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/inquiries`, { type: "request-quote", company: "", ...form });
      setDone(true);
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="relative bg-nx-navy2 border-b border-white/10 py-14 overflow-hidden" data-testid="home-quick-quote">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,rgba(189,137,49,0.08),transparent_55%)]" />
      <div className="relative nx-wrap">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end gap-8">
            <div className="lg:w-80 shrink-0">
              <p className="text-[10px] tracking-[0.28em] uppercase text-nx-goldlight mb-2">Quick Quote</p>
              <h2 className="font-marcellus text-2xl sm:text-3xl text-white leading-snug">Get a price in 24 hours.</h2>
              {done && <p className="mt-3 text-sm text-nx-goldlight" data-testid="quick-quote-success">Received — our sales team will reach out shortly.</p>}
            </div>
            <form onSubmit={submit} className="flex-1 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="quick-quote-form">
              <input required minLength={1} maxLength={120} placeholder="Full Name *" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="quick-quote-name" />
              <input required type="tel" maxLength={16} placeholder="Phone *" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d+\s-]/g, "") })} data-testid="quick-quote-phone" />
              <input required type="email" maxLength={160} placeholder="Email *" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="quick-quote-email" />
              <input required minLength={1} maxLength={200} placeholder="Requirement (e.g. 8 CCTV cameras) *" className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="quick-quote-requirement" />
              <button type="submit" disabled={busy} className="sm:col-span-2 lg:col-span-4 group inline-flex items-center justify-center gap-3 bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold text-sm tracking-wide px-9 py-3.5 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60" data-testid="quick-quote-submit">
                {busy ? "Sending…" : "Request a Quote"} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Hero = () => {
  const { scrollY } = useScroll();
  const yCanvas = useTransform(scrollY, [0, 900], [0, 160]);
  const fade = useTransform(scrollY, [0, 550], [1, 0.15]);

  return (
    <section id="top" className="relative min-h-svh flex items-start overflow-hidden bg-nx-navy" data-testid="home-hero">
      <motion.div style={{ y: yCanvas }} className="absolute inset-0">
        <img src="/images/hero-city-night.webp" alt="Technopark Trivandrum at night — Kerala's technology hub" className="absolute inset-0 w-full h-full object-cover object-bottom opacity-50" fetchPriority="high" data-testid="home-hero-photo" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,#10203a_0%,#0a1526_55%,#060d1a_100%)] opacity-75" />
        <div className="absolute inset-0 bg-gradient-to-r from-nx-navy via-nx-navy/70 to-nx-navy/30" />
        <NetworkCanvas className="absolute inset-0 w-full h-full" skyline />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(6,13,26,0.75)_100%)]" />
      </motion.div>
      <motion.div style={{ opacity: fade }} className="relative nx-wrap pt-20 pb-16 sm:pt-24 lg:pt-36 lg:pb-24 w-full">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-6 items-center">
        <div className="max-w-4xl">
          <FadeIn delay={0.15}>
            <div className="flex items-start gap-4 mb-7" data-testid="home-hero-eyebrow">
              <span className="h-px w-12 bg-nx-gold/70 mt-1.5 shrink-0" />
              <span className="text-[10px] sm:text-xs leading-relaxed tracking-[0.16em] sm:tracking-[0.24em] uppercase text-nx-goldlight">Integrated Technology Solutions & IT Supply Company</span>
            </div>
          </FadeIn>
          <h1 className="font-marcellus text-4xl sm:text-6xl lg:text-7xl text-white leading-[1.12] sm:leading-[1.08]" data-testid="home-hero-title">
            <MaskedLine delay={0.3}>NexSecure</MaskedLine>
            <MaskedLine delay={0.5} innerClassName="text-nx-goldlight">Technologies</MaskedLine>
          </h1>
          <FadeIn delay={0.8}>
            <p className="mt-5 text-sm leading-relaxed md:text-lg text-nx-goldlight font-medium tracking-wide" data-testid="home-hero-subheading">
              Software Services <span className="text-nx-gold/60 mx-1">|</span> Integrated Security <span className="text-nx-gold/60 mx-1">|</span> Smart Infrastructure <span className="text-nx-gold/60 mx-1">|</span> Automation <span className="text-nx-gold/60 mx-1">|</span> Managed Services
            </p>
          </FadeIn>
          <FadeIn delay={0.95}>
            <div className="mt-8 flex flex-wrap gap-x-7 gap-y-4" data-testid="home-hero-services">
              {HERO_SERVICES.map((s) => {
                const Icon = s.icon;
                return (
                  <span key={s.label} className="flex items-center gap-2.5 text-xs sm:text-sm text-slate-300" data-testid={`home-hero-service-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <Icon size={18} className="text-nx-goldlight" strokeWidth={1.5} /> {s.label}
                  </span>
                );
              })}
            </div>
          </FadeIn>
          <FadeIn delay={1.05}>
            <p className="mt-8 flex items-center gap-2 text-sm text-slate-400" data-testid="home-hero-kerala">
              <MapPin size={15} className="text-nx-goldlight" /> Serving Businesses Across Kerala
            </p>
          </FadeIn>
          <FadeIn delay={1.15}>
            <div className="mt-9 flex flex-col sm:flex-row flex-wrap gap-4">
              <Link to="/consultation" data-testid="home-hero-cta-quote" className="group inline-flex items-center justify-center gap-3 bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold text-sm tracking-wide px-9 py-4 transition-all duration-300 hover:-translate-y-0.5">
                Get a Quote <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/consultation" data-testid="home-hero-cta-visit" className="group inline-flex items-center justify-center gap-3 border border-nx-gold/70 text-nx-goldlight hover:bg-nx-gold/10 font-semibold text-sm tracking-wide px-9 py-4 transition-all duration-300 hover:-translate-y-0.5">
                Book a Site Visit <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </FadeIn>
        </div>
        <FadeIn delay={0.4} className="relative">
          <div className="relative mx-auto max-w-[460px] aspect-square" data-testid="home-hero-visual">
            <div className="absolute inset-8 rounded-full bg-nx-gold/15 blur-3xl" />
            <HeroGlobe className="absolute inset-0" accent="gold" />
            <p className="absolute bottom-[8%] right-[4%] text-right text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-nx-goldlight leading-[1.9]" data-testid="home-hero-globe-caption">
              Rooted in Kochi<br /><span className="text-slate-400">Serving all of Kerala</span>
            </p>
          </div>
        </FadeIn>
        </div>
      </motion.div>
      <button onClick={() => scrollToId("chapter-capabilities")} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500 hover:text-nx-goldlight transition-colors" aria-label="Scroll down" data-testid="home-scroll-cue">
        <ChevronDown size={20} className="animate-bounce" />
      </button>
    </section>
  );
};

const Capabilities = () => (
  <section id="chapter-capabilities" className="bg-nx-navy2 border-y border-white/10 py-20 sm:py-28" data-testid="home-capabilities">
    <div className="nx-wrap">
      <Chapter no="01" eyebrow="Core Capabilities" title="One partner, end to end." text="Security, networking, communication, automation and IT — engineered to work as one system, from design to long-term support." className="mb-14" />
      <Stagger className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10">
        {CAPABILITIES.map((c, i) => {
          const Icon = CAP_ICONS[i];
          return (
            <StaggerItem key={c.title}>
              <div className="group h-full bg-nx-navy2 hover:bg-nx-navy p-8 transition-colors duration-300" data-testid={`home-capability-${i}`}>
                <Icon size={26} className="text-nx-goldlight mb-6" strokeWidth={1.5} />
                <h3 className="font-marcellus text-lg text-white mb-3">{c.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{c.text}</p>
                <span className="block mt-6 h-px w-8 bg-nx-gold/50 group-hover:w-full transition-all duration-500" />
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>
      <FadeIn>
        <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 justify-center border border-white/10 bg-white/[0.02] px-6 py-4" data-testid="home-delivery-model">
          {["Supply", "Installation", "Integration", "Configuration", "Support", "AMC"].map((s, i) => (
            <span key={s} className="text-[10px] sm:text-[11px] tracking-[0.22em] uppercase text-slate-300">
              {i > 0 && <span className="text-nx-goldlight mx-3">•</span>}{s}
            </span>
          ))}
        </div>
      </FadeIn>
    </div>
  </section>
);

const Solutions = () => (
  <section className="bg-nx-navy py-20 sm:py-28" data-testid="home-solutions">
    <div className="nx-wrap">
      <Chapter no="02" eyebrow="Solutions" title="Ten solution areas. One integrated architecture." text="Every system we design — from surveillance to structured cabling — is engineered to work as part of a single, manageable whole." className="mb-14" />
      <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
        {SOLUTION_DETAILS.map((s, i) => (
          <Link key={s.slug} to={`/solutions/${s.slug}`} className="group flex items-start gap-6 bg-nx-navy hover:bg-nx-navy2 p-6 sm:p-7 transition-colors duration-300" data-testid={`home-solution-${i}`}>
            <span className="font-marcellus text-nx-gold/70 text-lg pt-0.5">{String(i + 1).padStart(2, "0")}</span>
            <span className="flex-1">
              <span className="block text-white text-base font-medium tracking-wide">{s.name}</span>
              <span className="block text-sm text-slate-500 mt-1.5 leading-relaxed">{s.tagline}</span>
            </span>
            <ArrowUpRight size={18} className="text-slate-600 group-hover:text-nx-goldlight group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all mt-1" />
          </Link>
        ))}
      </div>
      <Reveal delay={0.1} className="mt-10">
        <Link to="/solutions" data-testid="home-solutions-more" className="group inline-flex items-center gap-3 text-nx-goldlight text-xs tracking-[0.22em] uppercase">
          View all solutions in detail <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const ServicesOverview = () => (
  <section className="bg-nx-navy2 border-y border-white/10 py-20 sm:py-28" data-testid="home-services">
    <div className="nx-wrap">
      <Chapter no="03" eyebrow="Services" title="We don't disappear after installation." text="Sixteen services across one lifecycle: consult, survey, design, supply, install, integrate, commission, support, manage." className="mb-14" />
      <Stagger className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-px bg-white/10 border border-white/10">
        {SERVICES.slice(0, 9).map((s, i) => {
          const Icon = SERVICE_ICONS[i] || Compass;
          return (
            <StaggerItem key={s.slug}>
              <Link to={`/services/${s.slug}`} className="group flex flex-col items-start h-full bg-nx-navy2 hover:bg-nx-navy p-5 transition-colors" data-testid={`home-service-${i}`}>
                <Icon size={18} className="text-nx-goldlight mb-4" strokeWidth={1.5} />
                <span className="text-xs sm:text-sm text-slate-300 group-hover:text-white leading-snug transition-colors">{s.name}</span>
              </Link>
            </StaggerItem>
          );
        })}
      </Stagger>
      <Reveal delay={0.1} className="mt-10">
        <Link to="/services" data-testid="home-services-more" className="group inline-flex items-center gap-3 text-nx-goldlight text-xs tracking-[0.22em] uppercase">
          Explore the full service lifecycle <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const Industries = () => (
  <section className="bg-nx-navy py-20 sm:py-28" data-testid="home-industries">
    <div className="nx-wrap">
      <Chapter no="04" eyebrow="Industries" title="Wherever infrastructure matters." text="Offices, hospitals, hotels, campuses, factories and communities — each with its own demands, each engineered for specifically." className="mb-14" />
      <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
        {INDUSTRY_DETAILS.map((ind, i) => (
          <StaggerItem key={ind.slug}>
            <Link to={`/industries/${ind.slug}`} className="group flex flex-col justify-between h-full bg-nx-navy hover:bg-nx-navy2 p-6 sm:p-8 transition-colors duration-300 min-h-[10rem]" data-testid={`home-industry-${i}`}>
              <span className="font-marcellus text-nx-gold/50 text-sm">{String(i + 1).padStart(2, "0")}</span>
              <span className="flex items-end justify-between gap-3 mt-8">
                <span className="text-white text-sm sm:text-base font-medium leading-snug">{ind.name}</span>
                <ArrowUpRight size={16} className="text-slate-600 group-hover:text-nx-goldlight transition-colors shrink-0" />
              </span>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const WhyNexSecure = () => (
  <section className="bg-nx-navy2 border-y border-white/10 py-20 sm:py-28" data-testid="home-why">
    <div className="nx-wrap">
      <Chapter no="05" eyebrow="Why NexSecure" title="Chosen for how we work, not what we claim." className="mb-14" />
      <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {WHY.map((w, i) => {
          const Icon = w.icon;
          return (
            <StaggerItem key={w.title}>
              <div className="group h-full bg-nx-navy2 hover:bg-nx-navy p-8 transition-colors" data-testid={`home-why-${i}`}>
                <Icon size={22} className="text-nx-goldlight mb-5" strokeWidth={1.5} />
                <h3 className="font-marcellus text-lg text-white mb-3">{w.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{w.text}</p>
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>
    </div>
  </section>
);

const TechnologyBand = () => (
  <section className="bg-nx-navy py-20 sm:py-28" data-testid="home-technology">
    <div className="nx-wrap">
      <Chapter no="06" eyebrow="Technology Ecosystem" title="We engineer the solution. Technology follows." text="Requirements first — then the right manufacturers for performance, reliability, lifecycle and cost." className="mb-14" />
      <Stagger className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-px bg-white/10 border border-white/10">
        {TECH_CATEGORIES.map((c, i) => {
          const Icon = TECH_ICONS[i] || Blocks;
          return (
            <StaggerItem key={c.name}>
              <div className="flex flex-col items-start h-full bg-nx-navy p-5" data-testid={`home-tech-${i}`}>
                <Icon size={18} className="text-nx-goldlight mb-4" strokeWidth={1.5} />
                <span className="text-xs text-slate-300 leading-snug">{c.name}</span>
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>
      <Reveal delay={0.1} className="mt-10">
        <Link to="/technology" data-testid="home-technology-more" className="group inline-flex items-center gap-3 text-nx-goldlight text-xs tracking-[0.22em] uppercase">
          Our technology philosophy <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const FeaturedProjects = () => (
  <section className="bg-nx-navy2 border-y border-white/10 py-20 sm:py-28" data-testid="home-projects">
    <div className="nx-wrap">
      <Chapter no="07" eyebrow="Featured Projects" title="Work, documented." text="Requirement, challenge, design, implementation, result — every case study follows the same honest structure." className="mb-14" />
      <Stagger className="grid md:grid-cols-3 gap-6">
        {CASE_STUDIES.map((c) => (
          <StaggerItem key={c.slug}>
            <Link to={`/projects/${c.slug}`} className="group flex flex-col h-full border border-white/10 bg-white/[0.02] hover:border-nx-gold/40 transition-colors" data-testid={`home-project-${c.slug}`}>
              <div className="relative overflow-hidden">
                <img src={c.image} alt={c.title} className="w-full h-44 object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                <span className="absolute top-4 left-4 text-[10px] tracking-[0.22em] uppercase bg-nx-navy/85 backdrop-blur-sm text-nx-goldlight border border-nx-gold/40 px-3 py-1.5">{c.category}</span>
              </div>
              <div className="flex flex-col flex-1 p-6">
                <h3 className="font-marcellus text-lg text-white leading-snug">{c.title}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed flex-1">{c.location}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-nx-goldlight">Case study <ArrowUpRight size={13} /></span>
              </div>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const InstallationsPreview = () => {
  const preview = [
    { ...PROJECT_CATEGORIES[0].projects[0], category: PROJECT_CATEGORIES[0].name, image: PROJECT_CATEGORIES[0].image, slug: "office" },
    { ...PROJECT_CATEGORIES[2].projects[0], category: PROJECT_CATEGORIES[2].name, image: PROJECT_CATEGORIES[2].image, slug: "cctv" },
    { ...PROJECT_CATEGORIES[4].projects[0], category: PROJECT_CATEGORIES[4].name, image: PROJECT_CATEGORIES[4].image, slug: "networking" },
  ];
  return (
    <section className="bg-nx-navy py-20 sm:py-28" data-testid="home-installations">
      <div className="nx-wrap">
        <Chapter no="08" eyebrow="Installations" title="Completed installations across Kerala." text="Offices, apartments, campuses and factories — a preview of recent work by category." className="mb-14" />
        <Stagger className="grid md:grid-cols-3 gap-6">
          {preview.map((p) => (
            <StaggerItem key={p.title}>
              <div className="group flex flex-col h-full border border-white/10 bg-white/[0.02] hover:border-nx-gold/40 transition-colors duration-300" data-testid={`home-installation-${p.slug}`}>
                <div className="relative overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-44 object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <span className="absolute top-4 left-4 text-[10px] tracking-[0.22em] uppercase bg-nx-navy/85 backdrop-blur-sm text-nx-goldlight border border-nx-gold/40 px-3 py-1.5">{p.category}</span>
                </div>
                <div className="flex flex-col flex-1 p-6">
                  <h3 className="font-marcellus text-lg text-white leading-snug">{p.title}</h3>
                  <p className="mt-2 flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase text-nx-goldlight"><MapPin size={12} /> {p.location}</p>
                  <p className="text-sm text-slate-400 mt-3 leading-relaxed flex-1">{p.description}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal delay={0.15} className="mt-12">
          <Link to="/projects" data-testid="home-projects-view-all" className="group inline-flex items-center justify-center gap-3 bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold text-sm tracking-wide px-9 py-4 transition-all duration-300 hover:-translate-y-0.5">
            View All Projects <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const AmcBand = () => (
  <section className="relative bg-nx-navy py-20 sm:py-24 overflow-hidden" data-testid="home-amc">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,rgba(189,137,49,0.10),transparent_55%)]" />
    <div className="relative nx-wrap flex flex-col lg:flex-row lg:items-center gap-10 justify-between">
      <div className="max-w-xl">
        <Reveal>
          <span className="text-[11px] tracking-[0.3em] uppercase text-nx-goldlight">Managed Services & AMC</span>
          <h2 className="font-marcellus text-3xl sm:text-4xl text-white mt-4">Infrastructure that stays healthy — on schedule, not on luck.</h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-4 text-base text-slate-400 leading-relaxed">Preventive maintenance, priority breakdown response, health reports and lifecycle planning across IT, network, CCTV, access control, Wi-Fi and EPABX systems.</p>
        </Reveal>
      </div>
      <Reveal delay={0.15} className="flex flex-col sm:flex-row lg:flex-col gap-4 shrink-0">
        <Link to="/services/amc" data-testid="home-amc-link" className="group inline-flex items-center justify-center gap-3 border border-nx-gold/50 hover:border-nx-goldlight text-nx-goldlight text-sm tracking-wide px-9 py-4 transition-colors">
          About AMC <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
        <Link to="/services/managed-services" data-testid="home-managed-link" className="inline-flex items-center justify-center border border-white/25 hover:border-nx-goldlight/70 text-white text-sm tracking-wide px-9 py-4 transition-colors">Managed IT Services</Link>
      </Reveal>
    </div>
  </section>
);

const Coverage = () => (
  <section className="bg-nx-navy2 border-y border-white/10 py-20 sm:py-28" data-testid="home-coverage">
    <div className="nx-wrap grid lg:grid-cols-2 gap-14 items-center">
      <Reveal className="relative flex justify-center order-2 lg:order-1">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(212,175,94,0.08),transparent_65%)]" />
        <KeralaMap tone="dark" className="relative h-[380px] sm:h-[460px] w-auto" />
      </Reveal>
      <div className="order-1 lg:order-2">
        <Chapter no="08" eyebrow="Kerala Service Coverage" title="On-site technology assessments across Kerala." text="Serving businesses, institutions, industries, government organisations and communities across all fourteen districts — from our base in Ernakulam to every corner of the state." />
        <Reveal delay={0.2}>
          <div className="mt-8 flex flex-wrap gap-2" data-testid="home-district-list">
            {DISTRICTS.map((d) => (
              <span key={d} className="text-[11px] tracking-wider uppercase text-slate-300 border border-white/15 px-3 py-1.5 hover:border-nx-gold/50 hover:text-nx-goldlight transition-colors">{d}</span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/contact" data-testid="home-coverage-primary-cta" className="group inline-flex items-center justify-center gap-3 bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold text-sm tracking-wide px-9 py-4 transition-all duration-300 hover:-translate-y-0.5">
              Talk to Our Team <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link to="/solutions" data-testid="home-coverage-secondary-cta" className="inline-flex items-center justify-center border border-white/25 hover:border-nx-goldlight/70 text-white text-sm tracking-wide px-9 py-4 transition-colors">Explore Solutions</Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Process = () => (
  <section className="bg-nx-navy py-20 sm:py-28" data-testid="home-process">
    <div className="nx-wrap">
      <Chapter no="09" eyebrow="How We Work" title="A process built on accountability." className="mb-14" />
      <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
        {PROCESS.map((p) => (
          <StaggerItem key={p.no}>
            <div className="group h-full bg-nx-navy hover:bg-nx-navy2 p-8 transition-colors duration-300" data-testid={`home-process-${p.no}`}>
              <span className="font-marcellus text-4xl nx-text-outline">{p.no}</span>
              <h3 className="font-marcellus text-lg text-white mt-6 mb-3">{p.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{p.text}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const InsightsPreview = () => (
  <section className="bg-nx-navy2 border-y border-white/10 py-20 sm:py-28" data-testid="home-insights">
    <div className="nx-wrap">
      <Chapter no="10" eyebrow="Insights" title="Practical knowledge, from the field." className="mb-14" />
      <Stagger className="grid md:grid-cols-3 gap-6">
        {ARTICLES.slice(0, 3).map((a) => (
          <StaggerItem key={a.slug}>
            <Link to={`/insights/${a.slug}`} className="group flex flex-col h-full border border-white/10 bg-white/[0.02] hover:border-nx-gold/40 transition-colors p-7" data-testid={`home-insight-${a.slug}`}>
              <Newspaper size={20} className="text-nx-goldlight mb-5" strokeWidth={1.5} />
              <span className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-3">{a.category} · {a.readTime}</span>
              <h3 className="font-marcellus text-lg text-white leading-snug flex-1">{a.title}</h3>
              <span className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-nx-goldlight">Read <ArrowUpRight size={13} /></span>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const Enquiry = () => (
  <section id="enquiry" className="bg-nx-navy py-20 sm:py-28" data-testid="home-enquiry">
    <div className="nx-wrap grid lg:grid-cols-[1.6fr_1fr] gap-14">
      <div>
        <Chapter no="11" eyebrow="Start a Conversation" title="Request a site assessment." text="Tell us your solution area and a few lines about your project. Our team will take it from there — no long questionnaires." className="mb-10" />
        <Reveal delay={0.2}>
          <EnquiryForm tone="dark" source="home" buttonLabel="Request a Site Assessment" />
        </Reveal>
      </div>
      <div className="lg:pt-28">
        <Reveal delay={0.25}>
          <div className="border border-white/10 bg-white/[0.03] p-8">
            <p className="text-[10px] tracking-[0.28em] uppercase text-slate-400 mb-6">Prefer to reach us directly?</p>
            <ContactAside tone="dark" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid="home-page">
      <SiteHeader />
      <HolidayBanner />
      <Hero />
      <CompanyVideo />
      <QuickQuote />
      <Marquee />
      <Capabilities />
      <Solutions />
      <ServicesOverview />
      <TrustBand />
      <Industries />
      <WhyNexSecure />
      <TechnologyBand />
      <FeaturedProjects />
      <InstallationsPreview />
      <AmcBand />
      <Coverage />
      <Process />
      <InsightsPreview />
      <Enquiry />
      <CTABand />
      <SiteFooter />
    </div>
  );
}
